import React from 'react';
import PropTypes from 'prop-types';
import { Alert, Card, CardBody, CardHeader, Button, Row, Col } from 'reactstrap';
import { TransferItemsTable } from './TransferItemsTable';
import { TransactionLogPanel } from './TransactionLogPanel';
import { TransferStatusBadge } from '../../atoms/TransferStatusBadge';
import { calculateTransferStatus } from '../../../helpers/transferStatusCalculator';

class PsTransferViewContent extends React.Component {
    tableRef = null;

    state = {
        selectedItems: new Set(),
        transferDirections: new Map(), // productId -> 'toMarketplace' | 'toSaturn'
        isTransferring: false,
        transferError: null,
        transferSuccess: null
    };

    _handleSelectChange = (productId, isSelected) => {
        this.setState(prev => {
            const newSelected = new Set(prev.selectedItems);
            if (isSelected) {
                newSelected.add(productId);
            } else {
                newSelected.delete(productId);
            }
            return { selectedItems: newSelected };
        });
    };

    _handleDirectionChange = (productId) => {
        this.setState(prev => {
            const newDirections = new Map(prev.transferDirections);
            const current = newDirections.get(productId) || 'toMarketplace';
            newDirections.set(productId, current === 'toMarketplace' ? 'toSaturn' : 'toMarketplace');
            return { transferDirections: newDirections };
        });
    };

    _handleTransfer = async () => {
        const { po, client, onPoUpdated } = this.props;
        const { selectedItems, transferDirections } = this.state;

        if (selectedItems.size === 0) return;

        this.setState({ isTransferring: true, transferError: null, transferSuccess: null });

        try {
            // Get manual deltas from table if in danger mode
            const manualDeltas = this.tableRef ? this.tableRef.getManualDeltas() : {};

            // Build transfers array
            const transfers = [];
            selectedItems.forEach(productId => {
                const item = po.items.find(i => i.product.id === productId);
                if (!item) return;

                const direction = transferDirections.get(productId) || 'toMarketplace';
                let delta;

                if (manualDeltas[productId] !== undefined) {
                    // Use manual delta from danger mode
                    delta = manualDeltas[productId];
                } else {
                    // Calculate delta based on direction
                    delta = direction === 'toMarketplace' ? -item.quantity : item.quantity;
                }

                transfers.push({
                    productId: item.product.id,
                    lineItemId: item.id,
                    delta,
                    poQty: item.quantity,
                    productName: item.product.name
                });
            });

            if (transfers.length === 0) {
                this.setState({ isTransferring: false });
                return;
            }

            // Call API
            await client.receiving.applyPsTransfers({ poRef: po.ref, transfers });

            // Reset UI state
            this.setState({
                selectedItems: new Set(),
                transferDirections: new Map(),
                isTransferring: false,
                transferSuccess: `Successfully processed ${transfers.length} transfer(s)`
            });

            // Reset danger modes
            if (this.tableRef) {
                this.tableRef.resetDangerModes();
            }

            // Notify parent to refresh PO data
            if (onPoUpdated) {
                onPoUpdated();
            }
        } catch (error) {
            this.setState({
                isTransferring: false,
                transferError: error.message || 'Transfer failed'
            });
        }
    };

    _handleMarkCompleted = async () => {
        const { po, client, onPoUpdated } = this.props;

        try {
            const updatedPo = po.toJSON ? po.toJSON() : { ...po };
            updatedPo.meta = {
                ...updatedPo.meta,
                manuallyMarkedCompleted: true
            };
            await client.purchaseOrders.updatePurchaseOrder(updatedPo);

            if (onPoUpdated) {
                onPoUpdated();
            }
        } catch (error) {
            this.setState({
                transferError: error.message || 'Failed to mark as completed'
            });
        }
    };

    render() {
        const { po, productsClient } = this.props;
        const { selectedItems, transferDirections, isTransferring, transferError, transferSuccess } = this.state;

        const transactions = (po.meta && po.meta.piShopTransfer && po.meta.piShopTransfer.transactions) || [];
        const status = calculateTransferStatus(po);
        const isMarkedCompleted = po.meta && po.meta.manuallyMarkedCompleted;

        return (
            <div>
                {/* Warning Banner */}
                <Alert color="danger" className="mb-3">
                    <strong>SATURN AUTH NOT YET WORKING</strong>
                    <br />
                    <small>This UI cannot be used yet because Saturn API Auth Not Working for Inventory Transactions</small>
                </Alert>
                <Alert color="warning" className="mb-3">
                    <strong>THIS IS A MANUALLY INITIATED PROCESS</strong>
                    <br />
                    <small>Transfers will be sent directly to Saturn. Please verify selections before submitting.</small>
                </Alert>

                {/* Error/Success Messages */}
                {transferError && (
                    <Alert color="danger" toggle={() => this.setState({ transferError: null })}>
                        {transferError}
                    </Alert>
                )}
                {transferSuccess && (
                    <Alert color="success" toggle={() => this.setState({ transferSuccess: null })}>
                        {transferSuccess}
                    </Alert>
                )}

                {/* PO Header */}
                <Card className="mb-3">
                    <CardHeader>
                        <Row>
                            <Col>
                                <h5 className="mb-0">
                                    PO: {po.ref}
                                    <span className="ml-3">
                                        <TransferStatusBadge status={status} />
                                    </span>
                                </h5>
                            </Col>
                            <Col className="text-right">
                                {!isMarkedCompleted && (
                                    <Button
                                        color="success"
                                        onClick={this._handleMarkCompleted}
                                    >
                                        Mark Transfer Completed
                                    </Button>
                                )}
                            </Col>
                        </Row>
                    </CardHeader>
                    <CardBody>
                        <Row>
                            <Col md={4}>
                                <strong>Vendor:</strong> {(po.vendor && po.vendor.name) || 'N/A'}
                            </Col>
                            <Col md={4}>
                                <strong>Date:</strong> {po.purchaseDate ? new Date(po.purchaseDate).toLocaleDateString() : 'N/A'}
                            </Col>
                            <Col md={4}>
                                <strong>Items:</strong> {(po.items && po.items.length) || 0}
                            </Col>
                        </Row>
                    </CardBody>
                </Card>

                {/* Transfer Items Table */}
                <Card>
                    <CardHeader>
                        <h6 className="mb-0">Line Items</h6>
                    </CardHeader>
                    <CardBody>
                        <TransferItemsTable
                            ref={ref => { this.tableRef = ref; }}
                            items={po.items || []}
                            transactions={transactions}
                            selectedItems={selectedItems}
                            transferDirections={transferDirections}
                            onSelectChange={this._handleSelectChange}
                            onDirectionChange={this._handleDirectionChange}
                            onTransfer={this._handleTransfer}
                            isTransferring={isTransferring}
                            productsClient={productsClient}
                        />
                    </CardBody>
                </Card>

                {/* Transaction Log Panel */}
                <TransactionLogPanel transactions={transactions} />
            </div>
        );
    }
}

PsTransferViewContent.propTypes = {
    po: PropTypes.object.isRequired,
    client: PropTypes.object.isRequired,
    productsClient: PropTypes.object.isRequired,
    onPoUpdated: PropTypes.func
};

export { PsTransferViewContent };
